import React from 'react';
import { pdf } from '@react-pdf/renderer';
import TransmittalPdfDocument from './pdf/TransmittalPdfDocument';

/**
 * Renders a transmittal form to PDF and saves it. The Document component and the filename
 * prefix are parameters so every transmittal type (Submittal, RFI, Material Transfer) shares
 * the one save path; the default stays the Submittal Transmittal.
 *
 * Follows dsPdfExporter: open the native save dialog FIRST, while the click is still a user
 * gesture, then render, then write. Opening it after the render loses the gesture and the
 * browser refuses the picker.
 *
 * Rendered on the main thread rather than in a worker. The DS exporters use one because a
 * package is tens of sheets and takes 30-65 seconds; this is a single page of static text and
 * finishes in well under a second, so a worker would be machinery with nothing to carry.
 */
const exportTransmittalPdf = async ({
  data = {},
  Document = TransmittalPdfDocument,
  filenamePrefix = 'Submittal-Transmittal',
  watermark = null,
  onProgress,
}) => {
  try {
    if (onProgress) onProgress('Generating PDF…');

    const number   = data.transmittalNumber || 'transmittal';
    const filename = `${filenamePrefix}-${number}.pdf`.replace(/[/\\?%*:|"<>]/g, '-');

    let fileHandle = null;
    if (window.showSaveFilePicker) {
      try {
        fileHandle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [{ description: 'PDF Document', accept: { 'application/pdf': ['.pdf'] } }],
        });
      } catch (err) {
        // A cancelled picker is a decision, not a failure - leave without writing anything.
        if (err.name === 'AbortError') {
          if (onProgress) onProgress(null);
          return false;
        }
      }
    }

    const blob = await pdf(<Document data={data} watermark={watermark} />).toBlob();

    if (fileHandle) {
      try {
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
      } catch { fileHandle = null; }
    }

    if (!fileHandle) {
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }

    if (onProgress) onProgress(null);
    return true;
  } catch (err) {
    if (onProgress) onProgress(null);
    throw err;
  }
};

export default exportTransmittalPdf;
